import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import {
  Form,
  Input,
  Select,
  Card,
  message,
  Row,
  Col,
  Tooltip,
  Popconfirm,
  Button as AntButton,
} from 'antd';
import { Button } from 'components';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useLandingPages from '../useLandingPages';
import {
  buildTranslationRequest,
  applyTranslationToEditor,
} from '../editorContentTranslation';
import { TranslateFormLabel } from 'components/translateInputAddon';
import translateApi from 'libs/api/translate';
import UIHelper from 'helpers/UIHelper';
import grapesjs from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';
import basicBlocks from 'grapesjs-blocks-basic';
import websitePreset from 'grapesjs-preset-webpage';

const { Option } = Select;
const { TextArea } = Input;

export const LandingPageForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedShoppingCenter, setSelectedShoppingCenter] = useState(null);
  const [landingPageData, setLandingPageData] = useState(null);
  const [translatingContent, setTranslatingContent] = useState(false);

  const editorRefIT = useRef(null);
  const editorRefEN = useRef(null);
  const editorRefFR = useRef(null);
  const editorInstanceIT = useRef(null);
  const editorInstanceEN = useRef(null);
  const editorInstanceFR = useRef(null);
  const dataLoadedRef = useRef({ IT: false, EN: false, FR: false });
  const editorsReadyRef = useRef({ IT: false, EN: false, FR: false });
  const loadDataFnRef = useRef(null);

  const {
    shoppingCenters,
    loadingCompanies,
    createLandingPage,
    updateLandingPage,
    fetchLandingPage,
    uploadLandingAsset,
  } = useLandingPages();

  const isEditMode = !!id;

  const loadDataIntoEditors = useCallback(() => {
    if (!landingPageData) return;

    try {
      if (landingPageData.page_structure) {
        // Load IT editor
        if (
          landingPageData.page_structure.it &&
          editorInstanceIT.current &&
          editorsReadyRef.current.IT &&
          !dataLoadedRef.current.IT
        ) {
          const structure =
            typeof landingPageData.page_structure.it === 'string'
              ? JSON.parse(landingPageData.page_structure.it)
              : landingPageData.page_structure.it;

          if (structure.components)
            editorInstanceIT.current.setComponents(structure.components);
          if (structure.styles)
            editorInstanceIT.current.setStyle(structure.styles);
          dataLoadedRef.current.IT = true;
        }

        // Load EN editor
        if (
          landingPageData.page_structure.en &&
          editorInstanceEN.current &&
          editorsReadyRef.current.EN &&
          !dataLoadedRef.current.EN
        ) {
          const structure =
            typeof landingPageData.page_structure.en === 'string'
              ? JSON.parse(landingPageData.page_structure.en)
              : landingPageData.page_structure.en;

          if (structure.components)
            editorInstanceEN.current.setComponents(structure.components);
          if (structure.styles)
            editorInstanceEN.current.setStyle(structure.styles);
          dataLoadedRef.current.EN = true;
        }

        // Load FR editor
        if (
          landingPageData.page_structure.fr &&
          editorInstanceFR.current &&
          editorsReadyRef.current.FR &&
          !dataLoadedRef.current.FR
        ) {
          const structure =
            typeof landingPageData.page_structure.fr === 'string'
              ? JSON.parse(landingPageData.page_structure.fr)
              : landingPageData.page_structure.fr;

          if (structure.components)
            editorInstanceFR.current.setComponents(structure.components);
          if (structure.styles)
            editorInstanceFR.current.setStyle(structure.styles);
          dataLoadedRef.current.FR = true;
        }
      }
    } catch (e) {
      console.error('❌ Failed to load editor content:', e);
    }
  }, [landingPageData]);

  // Store function in ref so it can be called from editor event handlers
  loadDataFnRef.current = loadDataIntoEditors;

  const handleAssetUpload = useCallback(
    async (editor, event) => {
      const filesSource =
        event?.dataTransfer?.files ||
        event?.target?.files ||
        event?.files ||
        null;
      const itemsFromDataTransfer =
        !filesSource && event?.dataTransfer?.items
          ? Array.from(event.dataTransfer.items)
              .map((item) => (item.kind === 'file' ? item.getAsFile() : null))
              .filter(Boolean)
          : [];
      const fileList = filesSource
        ? Array.from(filesSource)
        : itemsFromDataTransfer;

      if (!fileList.length) return;

      const assetManager = editor.AssetManager;

      for (const file of fileList) {
        try {
          const result = await uploadLandingAsset(file);
          if (!result) {
            message.error(t('Errore durante il caricamento dell \'immagine'));
            continue;
          }

          const url =
            result?.url ||
            result?.src ||
            result?.data?.url ||
            result?.data?.src ||
            (Array.isArray(result?.data)
              ? result.data[0]?.url || result.data[0]?.src
              : null);

          if (url) {
            assetManager.add({ src: url, type: 'image' });
          } else {
            message.error(t('Errore durante il caricamento dell\'immagine'));
          }
        } catch (error) {
          console.error('Landing page asset upload failed:', error);
          message.error(t('Errore durante il caricamento dell\'immagine'));
        }
      }
    },
    [uploadLandingAsset, t],
  );

  const availableLanguages = useMemo(() => {
    if (!selectedShoppingCenter) return ['IT', 'EN'];
    const isTorino =
      selectedShoppingCenter.title?.toLowerCase().includes('torino') ||
      selectedShoppingCenter.name?.toLowerCase().includes('torino') ||
      selectedShoppingCenter.id === 'torino';
    return isTorino ? ['IT', 'EN', 'FR'] : ['IT', 'EN'];
  }, [selectedShoppingCenter]);

  const handleTranslateContent = useCallback(async () => {
    const editorIT = editorInstanceIT.current;
    if (!editorIT || translatingContent) return;

    const targetEditors = { en: editorInstanceEN.current };
    if (availableLanguages.includes('FR') && editorInstanceFR.current) {
      targetEditors.fr = editorInstanceFR.current;
    }

    const request = buildTranslationRequest(editorIT);

    if (!Object.keys(request.texts).length) {
      message.warning(t('Nessun testo da tradurre nel contenuto IT'));
      return;
    }

    setTranslatingContent(true);
    try {
      const targetLocales = Object.keys(targetEditors);
      const { data } = await translateApi.translate(
        request.texts,
        targetLocales,
      );
      const translations = data?.translations || {};

      targetLocales.forEach((locale) => {
        const editor = targetEditors[locale];
        const localeTexts = translations[locale];
        if (!editor || !localeTexts) return;

        applyTranslationToEditor(editor, request, localeTexts);
        dataLoadedRef.current[locale.toUpperCase()] = true;
      });
    } catch {
      UIHelper.openNotificationWithIcon('error', t('Traduzione non riuscita'));
    } finally {
      setTranslatingContent(false);
    }
  }, [availableLanguages, translatingContent, t]);

  // Initialize editors
  useEffect(() => {
    const initEditor = (containerRef, instanceRef, lang) => {
      if (!containerRef.current || instanceRef.current) {
        return;
      }

      const editor = grapesjs.init({
        container: containerRef.current,
        height: '600px',
        width: 'auto',
        fromElement: false,
        storageManager: false,
        plugins: [basicBlocks, websitePreset],
        pluginsOpts: {
          [basicBlocks]: { flexGrid: true },
          [websitePreset]: {},
        },
        canvas: {
          styles: [],
          scripts: [],
        },
        assetManager: {
          autoAdd: 1,
          uploadFile: (event) => handleAssetUpload(editor, event),
        },
      });

      // Mark editor as ready when fully loaded
      editor.on('load', () => {
        editorsReadyRef.current[lang] = true;

        // Try to load data if available and not yet loaded for this editor
        if (loadDataFnRef.current) {
          loadDataFnRef.current();
        }
      });

      // Add editable HTML/CSS code editor
      const pfx = editor.getConfig().stylePrefix;
      const modal = editor.Modal;
      const cmdm = editor.Commands;
      const codeViewer = editor.CodeManager.getViewer('CodeMirror').clone();
      const pnm = editor.Panels;
      const container = document.createElement('div');
      const btnEdit = document.createElement('button');

      codeViewer.set({
        codeName: 'htmlmixed',
        readOnly: 0,
        theme: 'hopscotch',
        autoBeautify: true,
        autoCloseTags: true,
        autoCloseBrackets: true,
        lineWrapping: true,
        styleActiveLine: true,
        smartIndent: true,
        indentWithTabs: true,
      });

      btnEdit.innerHTML = t('Save');
      btnEdit.type = 'button';
      btnEdit.className = pfx + 'btn-prim ' + pfx + 'btn-import';
      btnEdit.style.cssText = 'margin-top: 10px; padding: 8px 16px;';
      btnEdit.onclick = function (e) {
        e.preventDefault();
        e.stopPropagation();
        const code = codeViewer.editor.getValue();
        editor.DomComponents.getWrapper().set('content', '');
        editor.setComponents(code.trim());
        modal.close();
      };

      cmdm.add('html-edit', {
        run: function (editor, sender) {
          sender && sender.set('active', 0);
          let viewer = codeViewer.editor;
          modal.setTitle(t('Edit HTML/CSS Code'));
          if (!viewer) {
            const txtarea = document.createElement('textarea');
            container.appendChild(txtarea);
            container.appendChild(btnEdit);
            codeViewer.init(txtarea);
            viewer = codeViewer.editor;
          }
          const InnerHtml = editor.getHtml();
          const Css = editor.getCss();
          modal.setContent('');
          modal.setContent(container);
          codeViewer.setContent(InnerHtml + '<style>' + Css + '</style>');
          modal.open();
          viewer.refresh();
        },
      });

      pnm.addButton('options', [
        {
          id: 'edit',
          className: 'fa fa-edit',
          command: 'html-edit',
          attributes: {
            title: t('Edit Code'),
          },
        },
      ]);

      instanceRef.current = editor;
    };

    initEditor(editorRefIT, editorInstanceIT, 'IT');
    initEditor(editorRefEN, editorInstanceEN, 'EN');
    if (availableLanguages.includes('FR')) {
      initEditor(editorRefFR, editorInstanceFR, 'FR');
    }

    return () => {
      if (editorInstanceIT.current) editorInstanceIT.current.destroy();
      if (editorInstanceEN.current) editorInstanceEN.current.destroy();
      if (editorInstanceFR.current) editorInstanceFR.current.destroy();
      editorInstanceIT.current = null;
      editorInstanceEN.current = null;
      editorInstanceFR.current = null;
      editorsReadyRef.current = { IT: false, EN: false, FR: false };
      dataLoadedRef.current = { IT: false, EN: false, FR: false };
    };
  }, [availableLanguages, loadDataIntoEditors, handleAssetUpload, t]);

  // Load data in edit mode - step 1: load metadata and set shopping center
  useEffect(() => {
    if (!isEditMode || !id) return;

    const loadMetadata = async () => {
      setLoading(true);
      try {
        const landingPage = await fetchLandingPage(id);
        if (landingPage) {
          // Store data for later use
          setLandingPageData(landingPage);

          const shoppingCenterId =
            landingPage.shopping_center?.id || landingPage.shopping_center_id;
          const center = shoppingCenters.find(
            (c) => (c.value || c.id) === shoppingCenterId,
          );
          if (center) setSelectedShoppingCenter(center);

          form.setFieldsValue({
            title_it: landingPage.title_it,
            title_en: landingPage.title_en,
            title_fr: landingPage.title_fr,
            link_it: landingPage.link_it,
            link_en: landingPage.link_en,
            link_fr: landingPage.link_fr,
            meta_title_it: landingPage.meta_title_it,
            meta_title_en: landingPage.meta_title_en,
            meta_title_fr: landingPage.meta_title_fr,
            meta_description_it: landingPage.meta_description_it,
            meta_description_en: landingPage.meta_description_en,
            meta_description_fr: landingPage.meta_description_fr,
            meta_url_it: landingPage.meta_url_it,
            meta_url_en: landingPage.meta_url_en,
            meta_url_fr: landingPage.meta_url_fr,
            shopping_center_id: shoppingCenterId,
          });
        }
      } catch (e) {
        message.error(t('Failed to load landing page'));
      } finally {
        setLoading(false);
      }
    };

    loadMetadata();
  }, [isEditMode, id, fetchLandingPage, form, shoppingCenters, t]);

  // Load data in edit mode - step 2: trigger data load when data arrives
  useEffect(() => {
    if (!isEditMode || !landingPageData) return;

    loadDataIntoEditors();
  }, [isEditMode, landingPageData, loadDataIntoEditors]);

  const handleSubmit = useCallback(
    async (values) => {
      try {
        setSubmitting(true);
        if (!values.shopping_center_id || !values.title_it || !values.link_it) {
          message.error(
            t('Outlet Village, Titolo IT e Link IT sono obbligatori'),
          );
          return;
        }

        if (!editorInstanceIT.current || !editorInstanceEN.current) {
          message.error(t('Editor not initialized'));
          return;
        }

        const content = {};
        const page_structure = {};

        const htmlIT = editorInstanceIT.current.getHtml();
        const cssIT = editorInstanceIT.current.getCss();
        content.it = {
          html: `<style>${cssIT}</style>${htmlIT}`,
        };
        page_structure.it = {
          css: cssIT,
          components: editorInstanceIT.current.getComponents(),
          styles: editorInstanceIT.current.getStyle(),
        };

        const htmlEN = editorInstanceEN.current.getHtml();
        const cssEN = editorInstanceEN.current.getCss();
        content.en = {
          html: `<style>${cssEN}</style>${htmlEN}`,
        };
        page_structure.en = {
          css: cssEN,
          components: editorInstanceEN.current.getComponents(),
          styles: editorInstanceEN.current.getStyle(),
        };

        if (availableLanguages.includes('FR') && editorInstanceFR.current) {
          const htmlFR = editorInstanceFR.current.getHtml();
          const cssFR = editorInstanceFR.current.getCss();
          content.fr = {
            html: `<style>${cssFR}</style>${htmlFR}`,
          };
          page_structure.fr = {
            css: cssFR,
            components: editorInstanceFR.current.getComponents(),
            styles: editorInstanceFR.current.getStyle(),
          };
        }

        const payload = {
          title_it: values.title_it,
          title_en: values.title_en,
          title_fr: values.title_fr,
          link_it: values.link_it,
          link_en: values.link_en,
          link_fr: values.link_fr,
          meta_title_it: values.meta_title_it,
          meta_title_en: values.meta_title_en,
          meta_title_fr: values.meta_title_fr,
          meta_description_it: values.meta_description_it,
          meta_description_en: values.meta_description_en,
          meta_description_fr: values.meta_description_fr,
          meta_url_it: values.meta_url_it,
          meta_url_en: values.meta_url_en,
          meta_url_fr: values.meta_url_fr,
          shopping_center_id: values.shopping_center_id,
          content: content,
          page_structure: page_structure,
        };

        let result;
        if (isEditMode) {
          result = await updateLandingPage(id, payload);
        } else {
          result = await createLandingPage(payload);
        }

        if (result) {
          navigate('/website/landing_pages');
        }
      } catch (e) {
        console.error('Submit error:', e);
      } finally {
        setSubmitting(false);
      }
    },
    [
      isEditMode,
      id,
      createLandingPage,
      updateLandingPage,
      navigate,
      t,
      availableLanguages,
    ],
  );

  return (
    <Card
      title={isEditMode ? t('Modifica Landing Page') : t('Crea Landing Page')}
      extra={
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/website/landing_pages')}
        >
          {t('Indietro')}
        </Button>
      }
    >
      <Form form={form} layout='vertical' onFinish={handleSubmit}>
        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 16, fontWeight: 600, fontSize: 16 }}>
            {t('Metadati')}
          </div>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name='shopping_center_id'
                label={t('Outlet Village')}
                rules={[
                  { required: true, message: t('Seleziona un Outlet Village') },
                ]}
              >
                <Select
                  placeholder={t('Seleziona Outlet Village')}
                  loading={loadingCompanies}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={(value) => {
                    const center = shoppingCenters.find(
                      (c) => (c.value || c.id) === value,
                    );
                    setSelectedShoppingCenter(center);
                  }}
                >
                  {shoppingCenters
                    .filter(
                      (center) =>
                        center &&
                        (center.value || center.id) &&
                        (center.title || center.name),
                    )
                    .map((center) => (
                      <Option
                        key={center.value || center.id}
                        value={center.value || center.id}
                      >
                        {center.title || center.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={availableLanguages.length === 3 ? 8 : 12}>
              <Form.Item
                name='title_it'
                label={
                  <TranslateFormLabel
                    label={`${t('Titolo')} IT`}
                    form={form}
                    sourceField='title_it'
                    textKey='title'
                    targetFields={{ en: 'title_en', fr: 'title_fr' }}
                  />
                }
                rules={[
                  { required: true, message: t('Il titolo è obbligatorio') },
                ]}
              >
                <Input placeholder={t('Inserisci il titolo')} />
              </Form.Item>
            </Col>
            <Col span={availableLanguages.length === 3 ? 8 : 12}>
              <Form.Item name='title_en' label={`${t('Titolo')} EN`}>
                <Input placeholder={t('Inserisci il titolo')} />
              </Form.Item>
            </Col>
            {availableLanguages.includes('FR') && (
              <Col span={8}>
                <Form.Item name='title_fr' label={`${t('Titolo')} FR`}>
                  <Input placeholder={t('Inserisci il titolo')} />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Row gutter={16}>
            <Col span={availableLanguages.length === 3 ? 8 : 12}>
              <Form.Item
                name='link_it'
                label={`${t('Link')} IT`}
                rules={[
                  { required: true, message: t('Il link è obbligatorio') },
                ]}
              >
                <Input placeholder={t('Inserisci il link')} />
              </Form.Item>
            </Col>
            <Col span={availableLanguages.length === 3 ? 8 : 12}>
              <Form.Item name='link_en' label={`${t('Link')} EN`}>
                <Input placeholder={t('Inserisci il link')} />
              </Form.Item>
            </Col>
            {availableLanguages.includes('FR') && (
              <Col span={8}>
                <Form.Item name='link_fr' label={`${t('Link')} FR`}>
                  <Input placeholder={t('Inserisci il link')} />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Row gutter={16}>
            <Col span={availableLanguages.length === 3 ? 8 : 12}>
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: t('Inserisci il meta title obbligatorio'),
                  },
                ]}
                name='meta_title_it'
                label={
                  <TranslateFormLabel
                    label={`${t('Meta Title')} IT`}
                    form={form}
                    sourceField='meta_title_it'
                    textKey='meta_title'
                    targetFields={{ en: 'meta_title_en', fr: 'meta_title_fr' }}
                  />
                }
              >
                <Input placeholder={t('Inserisci il meta title per SEO')} />
              </Form.Item>
            </Col>
            <Col span={availableLanguages.length === 3 ? 8 : 12}>
              <Form.Item name='meta_title_en' label={`${t('Meta Title')} EN`}>
                <Input placeholder={t('Inserisci il meta title per SEO')} />
              </Form.Item>
            </Col>
            {availableLanguages.includes('FR') && (
              <Col span={8}>
                <Form.Item name='meta_title_fr' label={`${t('Meta Title')} FR`}>
                  <Input placeholder={t('Inserisci il meta title per SEO')} />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Row gutter={16}>
            <Col span={availableLanguages.length === 3 ? 8 : 12}>
              <Form.Item
                name='meta_description_it'
                label={
                  <TranslateFormLabel
                    label={`${t('Meta Description')} IT`}
                    form={form}
                    sourceField='meta_description_it'
                    textKey='meta_description'
                    targetFields={{
                      en: 'meta_description_en',
                      fr: 'meta_description_fr',
                    }}
                  />
                }
                rules={[
                  {
                    required: true,
                    message: t('Inserisci la meta description obbligatorio'),
                  },
                ]}
              >
                <TextArea
                  rows={3}
                  placeholder={t('Inserisci la meta description per SEO')}
                  maxLength={160}
                  showCount
                />
              </Form.Item>
            </Col>
            <Col span={availableLanguages.length === 3 ? 8 : 12}>
              <Form.Item
                name='meta_description_en'
                label={`${t('Meta Description')} EN`}
              >
                <TextArea
                  rows={3}
                  placeholder={t('Inserisci la meta description per SEO')}
                  maxLength={160}
                  showCount
                />
              </Form.Item>
            </Col>
            {availableLanguages.includes('FR') && (
              <Col span={8}>
                <Form.Item
                  name='meta_description_fr'
                  label={`${t('Meta Description')} FR`}
                >
                  <TextArea
                    rows={3}
                    placeholder={t('Inserisci la meta description per SEO')}
                    maxLength={160}
                    showCount
                  />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Row gutter={16}>
            <Col span={availableLanguages.length === 3 ? 8 : 12}>
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: t('Inserisci il meta url obbligatorio'),
                  },
                ]}
                name='meta_url_it'
                label={`${t('Meta URL')} IT`}
              >
                <Input placeholder='landing-page-url' />
              </Form.Item>
            </Col>
            <Col span={availableLanguages.length === 3 ? 8 : 12}>
              <Form.Item name='meta_url_en' label={`${t('Meta URL')} EN`}>
                <Input placeholder='landing-page-url' />
              </Form.Item>
            </Col>
            {availableLanguages.includes('FR') && (
              <Col span={8}>
                <Form.Item name='meta_url_fr' label={`${t('Meta URL')} FR`}>
                  <Input placeholder='landing-page-url' />
                </Form.Item>
              </Col>
            )}
          </Row>
        </div>

        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              marginBottom: 16,
              fontWeight: 600,
              fontSize: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {t('Contenuto IT')}
            <Popconfirm
              title={t(
                'Il contenuto EN/FR verrà sostituito con la traduzione del contenuto IT. Continuare?',
              )}
              okText={t('Sì')}
              cancelText={t('No')}
              onConfirm={handleTranslateContent}
            >
              <Tooltip
                title={t(
                  'Inserisci contenuti in italiano, e poi clicca per tradurere sulle varie lingue',
                )}
              >
                <AntButton
                  type='primary'
                  size='small'
                  htmlType='button'
                  icon={<RobotOutlined />}
                  loading={translatingContent}
                  style={{ fontSize: 10, padding: '0 7px' }}
                >
                  {t('Traduci con AI')}
                </AntButton>
              </Tooltip>
            </Popconfirm>
          </div>
          <div ref={editorRefIT} style={{ minHeight: '600px' }}></div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 16, fontWeight: 600, fontSize: 16 }}>
            {t('Contenuto EN')}
          </div>
          <div ref={editorRefEN} style={{ minHeight: '600px' }}></div>
        </div>

        {availableLanguages.includes('FR') && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 16, fontWeight: 600, fontSize: 16 }}>
              {t('Contenuto FR')}
            </div>
            <div ref={editorRefFR} style={{ minHeight: '600px' }}></div>
          </div>
        )}

        <Form.Item>
          <Button
            type='primary'
            width={'150px'}
            big
            htmlType='submit'
            loading={loading || submitting}
            icon={<SaveOutlined />}
            style={{ backgroundColor: '#002637', borderColor: '#002637' }}
          >
            {isEditMode ? t('Aggiorna') : t('Crea')}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};
